import { Component, Input, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { FinanceApiService, PlatformPaymentTermRecord } from '../../features/finance/finance-api.service';

interface PaymentTermOption {
  id: string;
  termCode: string;
  name: string;
  label: string;
  isActive: boolean;
}

@Component({
  standalone: true,
  selector: 'ain-payment-term-selector',
  imports: [FormsModule, SelectModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PaymentTermSelectorComponent),
      multi: true
    }
  ],
  template: `
    <p-select
      class="selector"
      [options]="options()"
      optionLabel="label"
      optionValue="id"
      [placeholder]="t(placeholderKey)"
      [showClear]="true"
      [filter]="true"
      filterBy="label,termCode,name"
      [disabled]="disabled || loading()"
      [loading]="loading()"
      [ngModel]="value()"
      (ngModelChange)="onValueChange($event)"
      appendTo="body" />
  `,
  styles: [`
    .selector { width: 100%; }
  `]
})
export class PaymentTermSelectorComponent implements ControlValueAccessor {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  @Input() placeholderKey = 'platform.paymentTerms.selectPlaceholder';
  @Input() activeOnly = true;

  readonly loading = signal(false);
  readonly options = signal<PaymentTermOption[]>([]);
  readonly value = signal<string | null>(null);

  private readonly allOptions = signal<PaymentTermOption[]>([]);
  disabled = false;

  constructor() {
    this.load();
  }

  writeValue(value: string | null): void {
    this.value.set(value);
    this.applyFilter();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  onValueChange(value: string | null): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  private load(): void {
    this.loading.set(true);
    this.api.listPlatformPaymentTerms({ activeOnly: this.activeOnly, take: 500 }).subscribe({
      next: response => {
        this.allOptions.set(response.items.map(item => this.toOption(item)));
        this.applyFilter();
        this.loading.set(false);
      },
      error: () => {
        this.allOptions.set([]);
        this.options.set([]);
        this.loading.set(false);
      }
    });
  }

  private applyFilter(): void {
    const selectedId = this.value()?.trim() ?? '';
    const all = this.allOptions();

    let filtered = all.filter(item => !this.activeOnly || item.isActive);

    if (selectedId && !filtered.some(item => item.id === selectedId)) {
      const selected = all.find(item => item.id === selectedId);
      if (selected) {
        filtered = [selected, ...filtered];
      }
    }

    this.options.set(filtered);
  }

  private toOption(item: PlatformPaymentTermRecord): PaymentTermOption {
    return {
      id: item.id,
      termCode: item.termCode,
      name: item.name,
      label: `${item.termCode} - ${item.name}`,
      isActive: item.isActive
    };
  }
}
